import { data as currencyData } from 'currency-codes';
import getSymbolFromCurrency from 'currency-symbol-map';
import { ChevronDown } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import CustomScrollbar from './CustomScrollbar';

interface CurrencyDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  requiredIndicatorPosition?: string;
  className?: string;
  disabled?: boolean;
  disableStyling?: boolean;
}

// Process currencies alphabetically
const currencies = currencyData
  .filter((currency) => currency && currency.code && currency.currency)
  .map((currency) => currency.currency)
  .filter((name, index, self) => self.indexOf(name) === index)
  .sort();

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({
  value,
  onChange,
  placeholder = 'Choose Reward Currency',
  required = false,
  requiredIndicatorPosition = 'left-[calc(0.5rem+20ch)]',
  className,
  disabled = false,
  disableStyling,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState('left-0');

  const filteredCurrencies = currencies.filter((currency) =>
    currency.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Auto reset when no results found
  useEffect(() => {
    if (searchTerm && filteredCurrencies.length === 0) {
      const timer = setTimeout(() => {
        setSearchTerm('');
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [searchTerm, filteredCurrencies.length]);

  const selectedCurrency = currencyData.find((c) => c.code.toLowerCase() === value.toLowerCase());
  const displayValue = selectedCurrency ? (
    <>
      <span className="font-bold">{getSymbolFromCurrency(selectedCurrency.code) || '$'}</span>
      {` - ${selectedCurrency.currency} (${selectedCurrency.code.toUpperCase()})`}
    </>
  ) : (
    ''
  );

  const handleSelect = (currencyName: string) => {
    const currency = currencyData.find((c) => c.currency === currencyName);
    if (currency) {
      onChange(currency.code.toLowerCase());
    }
    setIsOpen(false);
    setSearchTerm('');
    setIsSearchMode(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setIsSearchMode(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setIsSearchMode(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const dropdownWidth = 300;

        if (rect.left + dropdownWidth > screenWidth - 20) {
          setDropdownPosition('right-0');
        } else {
          setDropdownPosition('left-0');
        }
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {isSearchMode ? (
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search currencies..."
          className={`w-full p-2 ${className ? 'rounded-xl' : 'rounded-lg'} border ${
            className ?? 'bg-[#4A082D33]'
          } focus:outline-none text-[#621C43]`}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
              setSearchTerm('');
              setIsSearchMode(false);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              if (!isOpen) {
                setIsSearchMode(false);
                setSearchTerm('');
              }
            }, 150);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={handleInputClick}
          disabled={disabled}
          className={
            !disableStyling
              ? `w-full p-2 rounded-lg ${
                  className ?? 'bg-[#4A082D33]'
                } focus:outline-none text-[#621C43] text-left flex justify-between items-center ${
                  disabled ? 'cursor-not-allowed bg-opacity-60' : 'cursor-pointer'
                }`
              : `pt-1 ${disabled ? 'cursor-not-allowed bg-opacity-60' : 'cursor-pointer'}`
          }
          // className={
          //   className
          //     ? className
          //     : !disableStyling
          //       ? `w-full p-2 rounded-lg border ${className ?? 'bg-[#4A082D33]'} focus:outline-none text-[#621C43] text-left flex justify-between items-center ${disabled ? 'cursor-not-allowed bg-opacity-60' : 'cursor-pointer'
          //       }`
          //       : `pt-1 ${disabled ? 'cursor-not-allowed bg-opacity-60' : 'cursor-pointer'}`
          // }
        >
          <span>{displayValue || placeholder}</span>
          {!disableStyling && (
            <ChevronDown
              size={18}
              className={`ml-2 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          )}
        </button>
      )}

      {/* Required Indicator */}
      {required && !value && (
        <span
          className={`absolute ${requiredIndicatorPosition} top-2 text-red-500 pointer-events-none`}
        >
          *
        </span>
      )}

      {isOpen && (
        <div
          className={`absolute ${dropdownPosition} 
          
          w-full bg-white rounded-md shadow-md z-[9998] py-2 max-h-96 overflow-hidden`}
        >
          <CustomScrollbar>
            {filteredCurrencies.map((currencyName) => (
              <label
                key={currencyName}
                className="flex items-center px-3 py-2 cursor-pointer hover:bg-[#F8EAF1] whitespace-nowrap"
              >
                <input
                  type="radio"
                  name="currency"
                  value={currencyName}
                  checked={selectedCurrency?.currency === currencyName}
                  onChange={() => handleSelect(currencyName)}
                  className="mr-2 accent-[#621C43]"
                />
                {currencyName}
              </label>
            ))}
            {filteredCurrencies.length === 0 && (
              <div className="px-3 py-2 text-gray-500 text-center">No currencies found</div>
            )}
          </CustomScrollbar>
        </div>
      )}
    </div>
  );
};

export default CurrencyDropdown;
